// ── FeatureFlagGate ─────────────────────────────────────────────────────────
// Gates child routes/components behind a runtime feature flag from
// ops.FeatureFlags. Use in router.tsx around new modules during gradual rollout.
//
// Falls back to a friendly "feature not yet available" page when off.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useFeatureFlagsStore } from '../store/featureFlagsStore'
import { EmptyState } from '../shared/ui'

interface FeatureFlagGateProps {
  flag: string
  children: ReactNode
  /** Where to redirect/link the user when the flag is off. Defaults to home. */
  fallbackHref?: string
  /** Show "loading" state while flags haven't loaded yet. Defaults true. */
  showLoading?: boolean
}

export default function FeatureFlagGate({
  flag,
  children,
  fallbackHref = '/',
  showLoading = true,
}: FeatureFlagGateProps) {
  const loaded = useFeatureFlagsStore(s => s.loaded)
  const isOn   = useFeatureFlagsStore(s => s.isOn(flag))

  if (!loaded && showLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-on-surface-variant">Lade Feature-Flags…</span>
      </div>
    )
  }

  if (!isOn) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <EmptyState
          icon="schedule"
          title="Diese Funktion ist noch nicht freigeschaltet"
          description="Frage IT.Admin oder Verwaltung.Leitung, dich für den Pilot freizuschalten."
        />
        <div className="mt-4">
          <Link to={fallbackHref} className="text-sm text-primary hover:underline">← Zurück</Link>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
